import { useEffect, useMemo, useState } from 'react';
import { useTodoStore } from '../../store/todoStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { formatDate } from '../../utils/time';
import { parseTodoDueDate } from '../../utils/todoDate';
import { Icon } from '../../utils/icons';
import { TodoStatus } from '../../types/todo';
import LoadingSpinner from '../LoadingSpinner';
import { useToastStore } from '../../store/toastStore';

interface EventTodoPanelProps {
  eventId: string;
  eventTitle: string;
}

export function EventTodoPanel({ eventId, eventTitle }: EventTodoPanelProps) {
  const { todos, fetchTodos, createTodo, updateTodo } = useTodoStore();
  const { isDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const tokens = getDesignTokens(isDarkMode);
  const [title, setTitle] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [pendingStatusIds, setPendingStatusIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (eventId) {
      fetchTodos({ eventId }).catch((error) => {
        console.error('Error fetching event todos:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load event todos', 'error');
      });
    }
  }, [eventId, fetchTodos, showToast]);

  const eventTodos = useMemo(() => todos.filter((t) => t.eventId === eventId), [todos, eventId]);

  const handleAdd = async () => {
    if (!title.trim()) return;
    if (isAdding) return;
    setIsAdding(true);
    try {
      await createTodo({
        title,
        eventId,
        eventTitle,
        isFamily: true,
        priority: 'medium',
      });
      setTitle('');
      showToast('Todo added to event', 'success');
      fetchTodos({ eventId }).catch((error) => {
        console.error('Error refreshing event todos:', error);
      });
    } catch (error) {
      console.error('Error creating event todo:', error);
      showToast(error instanceof Error ? error.message : 'Failed to create todo', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const toggleStatus = (todoId: string, status: TodoStatus) => {
    const next = status === 'open' ? 'done' : 'open';
    setPendingStatusIds((state) => ({ ...state, [todoId]: true }));
    updateTodo(todoId, { status: next })
      .then(() => {
        showToast(next === 'done' ? 'Todo marked done' : 'Todo reopened', 'success');
      })
      .catch((error) => {
        console.error('Error updating event todo:', error);
        showToast(error instanceof Error ? error.message : 'Failed to update todo', 'error');
      })
      .finally(() => {
        setPendingStatusIds((state) => {
          const nextState = { ...state };
          delete nextState[todoId];
          return nextState;
        });
      });
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold text-gray-800">Event To Dos</div>
      </div>

      <div className="flex items-center gap-1.5 mb-1.5">
        <input
          className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
          placeholder="Add a to do for this event"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <button
          className={`px-2 py-1 rounded ${tokens.button.primary} text-xs font-semibold active:scale-95 min-h-[32px] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
          onClick={handleAdd}
          disabled={isAdding}
        >
          {isAdding ? <LoadingSpinner size="sm" label="Adding todo" /> : 'Add'}
        </button>
      </div>

      <div className="max-h-48 overflow-auto space-y-1">
        {eventTodos.map((todo) => (
          <div
            key={todo.todoId}
            className="border border-gray-200 rounded px-2 py-1 flex items-start gap-1.5"
          >
            <button
              className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                todo.status === 'done' ? 'bg-green-100 border-green-300' : 'bg-white border-gray-300'
              }`}
              onClick={() => toggleStatus(todo.todoId, todo.status)}
              aria-label="Toggle status"
              disabled={pendingStatusIds[todo.todoId]}
            >
              {pendingStatusIds[todo.todoId] ? <LoadingSpinner size="sm" label="Updating todo" /> : <Icon name="check" size="sm" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900 truncate">{todo.title}</div>
              {todo.dueDateTime && (
                <div className="text-[10px] text-gray-500">{formatDate(parseTodoDueDate(todo.dueDateTime) || new Date(), 'short')}</div>
              )}
            </div>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
                todo.status === 'done' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
              }`}
            >
              {todo.status === 'done' ? 'Done' : 'Open'}
            </span>
          </div>
        ))}
        {eventTodos.length === 0 && (
          <div className="text-xs text-gray-500 text-center py-2">No to dos linked yet.</div>
        )}
      </div>
    </div>
  );
}
